import React, { useState, useEffect } from 'react';
import { DollarSign, Calendar, Clock, AlertTriangle, CheckCircle, Search } from '../../components/Icons';
import Navigation from '../../components/Navigation';
import { apiGet, apiPost, apiPut } from '../../utils/api';

const FREQUENCY_LABELS = { monthly: 'Monthly', biannual: 'Every 6 months', annual: 'Annually' };

const Payments = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [billingModalTenant, setBillingModalTenant] = useState(null);
  const [billingForm, setBillingForm] = useState({ billing_frequency: '', agreed_amount: '', next_payment_date: '' });

  const [paymentModalTenant, setPaymentModalTenant] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_date: new Date().toISOString().slice(0, 10), method: 'bank_transfer', note: '' });

  const [historyModalTenant, setHistoryModalTenant] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    fetchTenants();
  }, [currentPage]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: currentPage, per_page: 20 });
      const response = await apiGet(`/api/admin/tenants?${params}`);
      if (response.ok) {
        setTenants(response.data.tenants || []);
        setTotalPages(response.data.pages || 1);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTenants = tenants.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (t.contact_email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paymentStatus = (tenant) => {
    if (!tenant.billing_frequency || !tenant.next_payment_date) return { label: 'Not set', color: 'bg-gray-100 text-gray-600', Icon: Clock };
    const daysUntil = Math.ceil((new Date(tenant.next_payment_date) - new Date()) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) return { label: `Overdue ${Math.abs(daysUntil)}d`, color: 'bg-red-100 text-red-700', Icon: AlertTriangle };
    if (daysUntil <= 7) return { label: `Due in ${daysUntil}d`, color: 'bg-yellow-100 text-yellow-800', Icon: Clock };
    return { label: 'On track', color: 'bg-green-100 text-green-700', Icon: CheckCircle };
  };

  const openBillingModal = (tenant) => {
    setBillingForm({
      billing_frequency: tenant.billing_frequency || '',
      agreed_amount: tenant.agreed_amount ?? '',
      next_payment_date: tenant.next_payment_date || '',
    });
    setBillingModalTenant(tenant);
  };

  const saveBilling = async (e) => {
    e.preventDefault();
    try {
      const response = await apiPut(`/api/admin/tenants/${billingModalTenant.id}/billing`, {
        billing_frequency: billingForm.billing_frequency || null,
        agreed_amount: billingForm.agreed_amount === '' ? null : parseFloat(billingForm.agreed_amount),
        next_payment_date: billingForm.next_payment_date || null,
      });
      if (response.ok) {
        setBillingModalTenant(null);
        fetchTenants();
      } else {
        alert(response.data?.detail || 'Failed to update billing');
      }
    } catch (error) {
      console.error('Error updating billing:', error);
      alert('Error updating billing');
    }
  };

  const openPaymentModal = (tenant) => {
    setPaymentForm({ amount: tenant.agreed_amount || '', payment_date: new Date().toISOString().slice(0, 10), method: 'bank_transfer', note: '' });
    setPaymentModalTenant(tenant);
  };

  const savePayment = async (e) => {
    e.preventDefault();
    try {
      const response = await apiPost(`/api/admin/tenants/${paymentModalTenant.id}/payments`, {
        amount: parseFloat(paymentForm.amount),
        payment_date: paymentForm.payment_date,
        method: paymentForm.method,
        note: paymentForm.note || null,
      });
      if (response.ok) {
        setPaymentModalTenant(null);
        fetchTenants();
      } else {
        alert(response.data?.detail || 'Failed to record payment');
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      alert('Error recording payment');
    }
  };

  const openHistoryModal = async (tenant) => {
    setHistoryModalTenant(tenant);
    setHistoryLoading(true);
    try {
      const response = await apiGet(`/api/admin/tenants/${tenant.id}/payments`);
      setHistory(response.ok ? response.data.payments || [] : []);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  if (loading) {
    return (
      <Navigation>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading payments...</p>
          </div>
        </div>
      </Navigation>
    );
  }

  return (
    <Navigation>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Payments</h1>
                <p className="text-gray-600">Track manual bank-transfer billing agreed with each tenant</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Search tenants..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <button onClick={fetchTenants} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
                Refresh
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Tenant Billing</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agreed Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Payment</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTenants.map((tenant) => {
                    const status = paymentStatus(tenant);
                    return (
                      <tr key={tenant.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{tenant.name}</div>
                          <div className="text-xs text-gray-500">{tenant.contact_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {tenant.billing_frequency ? FREQUENCY_LABELS[tenant.billing_frequency] : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {tenant.agreed_amount != null ? `Rs. ${Number(tenant.agreed_amount).toLocaleString()}` : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            {tenant.next_payment_date || '—'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${status.color}`}>
                            <status.Icon className="h-3 w-3" />
                            {status.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-3">
                            <button onClick={() => openBillingModal(tenant)} className="text-blue-600 hover:text-blue-900" title="Edit billing terms">
                              Edit
                            </button>
                            <button onClick={() => openPaymentModal(tenant)} className="text-green-600 hover:text-green-900 flex items-center gap-1" title="Record a payment">
                              <DollarSign className="h-4 w-4" /> Record
                            </button>
                            <button onClick={() => openHistoryModal(tenant)} className="text-gray-600 hover:text-gray-900" title="Payment history">
                              History
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                <button
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
                <button
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit billing modal */}
      {billingModalTenant && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/5 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Billing terms - {billingModalTenant.name}</h3>
            <form onSubmit={saveBilling} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Billing frequency</label>
                <select
                  value={billingForm.billing_frequency}
                  onChange={(e) => setBillingForm({ ...billingForm, billing_frequency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Not set</option>
                  <option value="monthly">Monthly</option>
                  <option value="biannual">Every 6 months</option>
                  <option value="annual">Annually</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Agreed amount (Rs.)</label>
                <input
                  type="number" step="0.01" min="0"
                  value={billingForm.agreed_amount}
                  onChange={(e) => setBillingForm({ ...billingForm, agreed_amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Next payment date</label>
                <input
                  type="date"
                  value={billingForm.next_payment_date}
                  onChange={(e) => setBillingForm({ ...billingForm, next_payment_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" onClick={() => setBillingModalTenant(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Record payment modal */}
      {paymentModalTenant && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/5 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Record payment - {paymentModalTenant.name}</h3>
            {paymentModalTenant.billing_frequency && (
              <p className="text-sm text-gray-500 mb-4">
                Next payment date will auto-advance by one {FREQUENCY_LABELS[paymentModalTenant.billing_frequency].toLowerCase()} cycle from the date entered below.
              </p>
            )}
            <form onSubmit={savePayment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount received (Rs.)</label>
                <input
                  type="number" step="0.01" min="0" required
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment date</label>
                <input
                  type="date" required
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Method</label>
                <select
                  value={paymentForm.method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="bank_transfer">Bank transfer</option>
                  <option value="cash">Cash</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Note (transaction ref, bank, etc.)</label>
                <input
                  type="text"
                  value={paymentForm.note}
                  onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" onClick={() => setPaymentModalTenant(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                  Record payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* History modal */}
      {historyModalTenant && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Payment history - {historyModalTenant.name}</h3>
              <button onClick={() => setHistoryModalTenant(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            {historyLoading ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : history.length === 0 ? (
              <p className="text-sm text-gray-500">No payments recorded yet.</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Recorded by</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {history.map((p) => (
                    <tr key={p.id}>
                      <td className="px-3 py-2 text-sm text-gray-700">{p.payment_date}</td>
                      <td className="px-3 py-2 text-sm text-gray-900 font-medium">Rs. {Number(p.amount).toLocaleString()}</td>
                      <td className="px-3 py-2 text-sm text-gray-700">{p.method}</td>
                      <td className="px-3 py-2 text-sm text-gray-500">{p.note || '—'}</td>
                      <td className="px-3 py-2 text-sm text-gray-500">{p.recorded_by || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </Navigation>
  );
};

export default Payments;
